// Whether the fleet can serve a CONVERSATION at all, graded across every box the
// account can reach: the coarse gap a dashboard nudge is keyed by, and the one
// function that derives it.
// Split out of `../runner.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { runnerRoleCanConverse } from './role';
import type { Runner } from './row';
import { RunnerStatus } from './status';

/**
 * The two fields scout capacity is decided on — narrowed from {@link Runner} so a
 * grader can never reach a box's credentials or presence stats, and so a test can
 * state a whole fleet in one line. Both are published by BOTH doors (`box list`,
 * `GET /v1/runners`), which is what makes a refusal that names them re-readable by
 * the caller on either transport.
 */
export type ScoutBoxState = Pick<Runner, 'mode' | 'status'>;

/**
 * WHICH condition stops an account holding a live scout session — the answer
 * {@link scoutCapacityGap} grades and both `plan_start` doors render (arc 101
 * slice 06). Capacity is the CONJUNCTION `status = online ∧ runnerRoleCanConverse`,
 * so a refusal that only says "no capacity" leaves the caller to guess which half
 * failed; each member below names one half and has its own remedy and its own
 * answer to "does waiting clear this".
 *
 * The role members are two and not one because `mode` is only sometimes an
 * observation. `runners.mode` DEFAULTS to `UNADVERTISED_RUNNER_ROLE` (`./role.ts`) in the
 * column and enrolment writes no mode, while its writers —
 * `updateRunnerMode` (the presence sweep) and `reviveRunner`, both in
 * `@developerz/db` — copy the value out of the box's own presence advert. So any
 * value OTHER than the placeholder is proof the box advertised it, and the
 * placeholder itself proves nothing: same word, two states, opposite remedies.
 * That ambiguity is real in prod — on 2026-08-30 seven online boxes read `worker`
 * with a freshly synced `runner_version` (genuinely pinned), and a just-enrolled
 * row reads the same value having advertised nothing.
 */
export const ScoutCapacityGap = {
  /** The account holds no box at all. Cleared by enrolling one, never by waiting. */
  NoBoxes: 'no_boxes',
  /** Boxes exist, none is online, and at least one lapsed after running. */
  Offline: 'boxes_offline',
  /** Boxes exist, none is online, and none ever dialed in (`pending`). */
  NeverStarted: 'boxes_never_started',
  /** Every box is `revoked` — terminal; no wait and no restart clears it. */
  Revoked: 'boxes_revoked',
  /**
   * A box IS online, and its `mode` reads the column placeholder — which is both
   * the default an un-advertised row carries AND a role an operator can really
   * pin. Waiting for the next presence sweep is what tells the two apart.
   */
  RolePlaceholder: 'role_placeholder',
  /**
   * RETIRED — never graded any more. It named an online box advertising a role that
   * cannot converse and is not the placeholder; the only such roles were `monitor`,
   * `scout` and `setup`, all deleted from `RunnerRole` (a box now advertises
   * `worker` or `universal`, and `worker` IS the placeholder). Kept because it is a
   * value the platform already wrote: 22 audit rows in the 120 days to 2026-09-29 carry
   * `role_pinned` in a refusal payload.
   */
  RolePinned: 'role_pinned',
} as const;
export const ScoutCapacityGapSchema = z.enum(ScoutCapacityGap);
export type ScoutCapacityGap = z.infer<typeof ScoutCapacityGapSchema>;

/**
 * Can any of these boxes hold a live scout session right now, and if not, WHICH
 * condition failed? `null` is capacity; a {@link ScoutCapacityGap} is the refusal's
 * reason.
 *
 * ONE seam for both halves of the question, so "is there capacity" and "why not"
 * cannot disagree — the bug that shape prevents is a caller told to boot a box by a
 * refusal graded on a different rule than the one that refused it.
 *
 * ORDER: liveness is named only when NOTHING is online, and the role only when
 * something is. That is not a preference between two true answers — it is the only
 * ordering that terminates. `mode` is telemetry a box pushes while it beats, so on a
 * fleet that is entirely down the role half is unknowable and "your box is pinned
 * wrong" would be a guess; conversely once a box IS online, liveness is satisfied on
 * it and the role is the sole remaining gap. A caller that fixes the named half and
 * retries gets the other half's sentence at most once more.
 *
 * Pure — no I/O, so the `plan_start` port, the REST door and (arc 101 slice 12) the
 * readiness rung all grade the same fleet the same way.
 */
export function scoutCapacityGap(boxes: readonly ScoutBoxState[]): ScoutCapacityGap | null {
  const online = boxes.filter((b) => b.status === RunnerStatus.Online);
  if (online.some((b) => runnerRoleCanConverse(b.mode))) return null;
  if (boxes.length === 0) return ScoutCapacityGap.NoBoxes;
  // Online and none converses: every such box reads `worker`, the column placeholder —
  // the only role left that cannot converse (`RolePinned` is retired with the roles it
  // named). Waiting for the next presence sweep is what tells a placeholder from a pin.
  if (online.length > 0) return ScoutCapacityGap.RolePlaceholder;
  if (boxes.some((b) => b.status === RunnerStatus.Offline)) return ScoutCapacityGap.Offline;
  if (boxes.some((b) => b.status === RunnerStatus.Pending)) return ScoutCapacityGap.NeverStarted;
  // Only `revoked` is left: `boxes` is non-empty and the other three members of
  // `RunnerStatusSchema` are matched above. Enumerated rather than asserted —
  // `runner-scout-capacity.test.ts` walks `RunnerStatusSchema.options`, so a fifth
  // status fails the build here instead of silently reading as revoked.
  return ScoutCapacityGap.Revoked;
}
